/**
 * Messages for one area of the app (document 15, section 5.1), English and Arabic side by side.
 * Every key lives under `reportPages`; Arabic plurals have all six CLDR forms.
 */
import * as catalogue from './reportPages/catalogue.js';

export const en = {
  reportPages: {
    ...catalogue.en,
    reports: {
      catalogue: {
        names: {
          'profit-and-loss': 'Profit and loss',
          'profit-and-loss-by-class': 'Profit and loss by class',
          'profit-and-loss-by-location': 'Profit and loss by location',
          'profit-and-loss-by-tag-group': 'Profit and loss by tag group',
          'project-profitability': 'Project profitability summary',
          'balance-sheet': 'Balance sheet',
          'cash-flow': 'Statement of cash flows',
          'budget-vs-actuals': 'Budget vs actuals',
          'ar-aging-summary': 'Accounts receivable ageing summary',
          'ar-aging-detail': 'Accounts receivable ageing detail',
          'customer-balance-summary': 'Customer balance summary',
          'customer-balance-detail': 'Customer balance detail',
          'open-invoices': 'Open invoices',
          'sales-by-customer': 'Sales by customer summary',
          'sales-by-customer-detail': 'Sales by customer detail',
          'sales-by-item': 'Sales by product/service summary',
          'sales-by-item-detail': 'Sales by product/service detail',
          'ap-aging-summary': 'Accounts payable ageing summary',
          'ap-aging-detail': 'Accounts payable ageing detail',
          'vendor-balance-summary': 'Vendor balance summary',
          'vendor-balance-detail': 'Vendor balance detail',
          'inventory-valuation-summary': 'Inventory valuation summary',
          'inventory-valuation-detail': 'Inventory valuation detail',
          'physical-inventory-worksheet': 'Physical inventory worksheet',
          'open-purchase-order-list': 'Open purchase order list',
          'open-purchase-order-detail': 'Open purchase order detail',
          'unpaid-bills': 'Unpaid bills',
          'expenses-by-vendor': 'Expenses by vendor summary',
          'purchases-by-vendor-detail': 'Purchases by vendor detail',
          'purchases-by-item-detail': 'Purchases by product/service detail',
          'vat-201-summary': 'VAT 201 summary',
          'vat-summary': 'VAT summary',
          'vat-detail': 'VAT detail',
          'vat-exception': 'VAT exception',
          'gstr-1': 'GSTR-1',
          'gstr-3b': 'GSTR-3B',
          'gst-detail': 'GST detail',
          'itc-summary': 'Input tax credit summary',
          'hsn-summary': 'HSN/SAC summary',
          'trial-balance': 'Trial balance',
          'fixed-asset-register': 'Fixed asset register',
          'general-ledger': 'General ledger',
          'transaction-detail-by-account': 'Transaction detail by account',
          'transaction-list-by-date': 'Transaction list by date',
          journal: 'Journal',
          'account-list': 'Account list',
          'reconciliation-report': 'Reconciliation reports',
          'parallel-run': 'Parallel run comparison',
          'audit-log': 'Audit log',
          'voided-reversed': 'Voided and reversed transactions',
        },
        descriptions: {
          'profit-and-loss': 'Income and expenses, and the profit or loss they make, for a period.',
          'profit-and-loss-by-class':
            'Income and expenses for each class, with lines without a class and the total.',
          'profit-and-loss-by-location':
            'Income and expenses for each location, with lines without one and the total.',
          'profit-and-loss-by-tag-group':
            'Income and expenses for each tag of a group, with untagged lines and the total.',
          'project-profitability':
            'Income, costs and profit of each project, with lines without a project.',
          'balance-sheet':
            'What the company owns and owes, and its equity, at the end of a period.',
          'cash-flow':
            'Where cash came from and went: operating, investing and financing, with cash at the start and end.',
          'budget-vs-actuals':
            'Actual income and expenses against a budget, with the amount over budget and % of budget.',
          'ar-aging-summary':
            'What each customer owes you, by how many days the invoices are past due.',
          'ar-aging-detail': 'Each unpaid invoice and customer credit, grouped by days past due.',
          'customer-balance-summary': 'What each customer owes you as of a date.',
          'customer-balance-detail':
            'Each customer’s unpaid invoices and credits, with what is still open.',
          'open-invoices': 'Invoices that are not yet fully paid, with their due dates.',
          'sales-by-customer': 'Your sales to each customer for a period, before VAT.',
          'sales-by-customer-detail': 'Each sale to each customer for a period, line by line.',
          'sales-by-item':
            'Quantity, sales and average price of each product or service for a period.',
          'sales-by-item-detail':
            'Each sale of each product or service for a period, line by line.',
          'ap-aging-summary': 'What you owe each vendor, by how many days the bills are past due.',
          'ap-aging-detail': 'Each unpaid bill and vendor credit, grouped by days past due.',
          'vendor-balance-summary': 'What you owe each vendor as of a date.',
          'vendor-balance-detail':
            'Each vendor’s unpaid bills and credits, with what is still open.',
          'inventory-valuation-summary':
            'Quantity on hand, FIFO asset value and average cost of each inventory item.',
          'inventory-valuation-detail':
            'Every stock movement with the quantity and value on hand after it.',
          'physical-inventory-worksheet':
            'Inventory items with the quantity on hand and a column for the count.',
          'open-purchase-order-list':
            'Purchase orders not yet received in full, with what is still to come.',
          'open-purchase-order-detail':
            'Each open purchase order line: ordered, received and backordered quantities.',
          'unpaid-bills': 'Bills that are not yet fully paid, with their due dates.',
          'expenses-by-vendor': 'What you spent with each vendor for a period, before VAT.',
          'purchases-by-vendor-detail':
            'Each purchase from each vendor for a period, line by line.',
          'purchases-by-item-detail':
            'Each purchase of each product, service or category for a period, line by line.',
          'vat-201-summary': 'The VAT 201 return working for each period, box by box.',
          'vat-summary': 'Sales, purchases and VAT by VAT code for a period.',
          'vat-detail': 'Every line behind each VAT 201 box for a period.',
          'vat-exception':
            'Lines that need attention before filing: missing codes, TRNs, mismatches.',
          'gstr-1':
            'Outward supplies by GSTR-1 table: B2B, B2C, exports, nil rated and credit notes.',
          'gstr-3b':
            'Outward supplies, reverse charge, input tax credit and the tax payable in cash.',
          'gst-detail':
            'Every document behind the GST returns, with taxable value and IGST, CGST, SGST.',
          'itc-summary': 'Input tax credit by vendor and bill, including reverse charge.',
          'hsn-summary':
            'Outward supplies by HSN or SAC code and rate, B2B and B2C (GSTR-1 table 12).',
          'trial-balance':
            'Every account’s debit or credit balance as of a date; the columns agree.',
          'fixed-asset-register':
            'Each fixed asset with its cost, depreciation booked and net book value as of a date.',
          'general-ledger':
            'Every transaction by account, from the opening balance to the closing balance.',
          'transaction-detail-by-account':
            'The transactions behind each account’s activity in a period.',
          'transaction-list-by-date': 'One line per transaction, in date order.',
          journal: 'Every transaction with its debit and credit lines.',
          'account-list': 'The chart of accounts with types, descriptions and balances.',
          'reconciliation-report':
            'Each finished reconciliation with its cleared and uncleared items.',
          'parallel-run':
            'QuickBooks trial balance, P&L, balance sheet and ageing beside Outss Accounting, with explanations.',
          'audit-log': 'Who changed what, and when, with the values before and after.',
          'voided-reversed':
            'Reversed transactions with their reversal, who reversed them and why.',
        },
        categories: {
          businessOverview: 'Business overview',
          whoOwesYou: 'Who owes you',
          salesAndCustomers: 'Sales and customers',
          whatYouOwe: 'What you owe',
          expensesAndVendors: 'Expenses and vendors',
          vat: 'VAT',
          gst: 'GST',
          forMyAccountant: 'For my accountant',
          audit: 'Audit',
        },
      },
      period: {
        preset: {
          all: 'All dates',
          custom: 'Custom',
          today: 'Today',
          this_week: 'This week',
          this_week_to_date: 'This week to date',
          this_month: 'This month',
          this_month_to_date: 'This month to date',
          this_quarter: 'This quarter',
          this_quarter_to_date: 'This quarter to date',
          this_year: 'This financial year',
          this_year_to_date: 'This financial year to date',
          yesterday: 'Yesterday',
          last_week: 'Last week',
          last_month: 'Last month',
          last_quarter: 'Last quarter',
          last_year: 'Last financial year',
          last_7_days: 'Last 7 days',
          last_30_days: 'Last 30 days',
        },
        endOf: {
          this_month: 'End of this month',
          this_quarter: 'End of this quarter',
          this_year: 'End of this financial year',
          last_month: 'End of last month',
          last_quarter: 'End of last quarter',
          last_year: 'End of last financial year',
        },
        endDateRequired: 'Choose the end date.',
        endBeforeStart: 'The end date is before the start date.',
        reportPeriod: 'Report period',
        asOf: 'As of',
        from: 'From',
        to: 'To',
      },
      hub: {
        title: 'Reports',
        tabStandard: 'Standard',
        tabCustom: 'Custom reports',
        tabCustomWithCount: 'Custom reports ({{count}})',
        tabManagement: 'Management reports',
        find: 'Find report by name',
        favourites: 'Favourites',
        addFavourite: 'Add {{name}} to favourites',
        removeFavourite: 'Remove {{name}} from favourites',
        comingSoon: 'Coming soon',
      },
      custom: {
        couldNotLoad: 'Could not load custom reports',
        emptyTitle: 'No custom reports yet',
        emptyHint: 'Run a report, change it with Customise, then choose Save customisation.',
        standardReports: 'Standard reports',
        colName: 'Name',
        colReport: 'Report',
        colSharedWith: 'Shared with',
        colCreatedBy: 'Created by',
        colLastModified: 'Last modified',
        colAction: 'Action',
        delete: 'Delete',
        noMatch: 'No custom report matches.',
        deleteTitle: 'Delete custom report?',
        deleteBody:
          '“{{name}}” is removed for everyone it is shared with. The report itself and the books are not affected.',
        deleted: 'Custom report "{{name}}" deleted',
      },
      route: {
        notFound: 'Report not found',
        notAvailable: "This feature isn't available yet.",
        noSuchReport: 'There is no report called {{key}}.',
        backToList: 'Back to report list',
        couldNotLoadPack: 'Could not load the management report',
      },
      page: {
        budget: 'Budget',
        accountingMethod: 'Accounting method',
        accrual: 'Accrual',
        cash: 'Cash',
        expand: 'Expand',
        collapse: 'Collapse',
        customise: 'Customise',
        saveCustomisation: 'Save customisation',
        email: 'Email',
        print: 'Print',
        export: 'Export',
        exportExcel: 'Export to Excel',
        exportPdf: 'Export to PDF',
        exportCsv: 'Export to CSV',
        exportFailed: 'Export failed: {{message}}',
        couldNotRun: 'Could not run the report',
        nothingToShow: 'Nothing to show for this period.',
        accrualBasis: 'Accrual basis',
        generated: 'Generated {{when}}',
        tagGroup: 'Tag group',
        noTagGroups: 'No tag groups yet',
      },
      customise: {
        title: 'Customise report',
        tabs: {
          general: 'General',
          rowsColumns: 'Rows/Columns',
          filter: 'Filter',
          headerFooter: 'Header/footer',
        },
        numberFormat: 'Number format',
        divideBy1000: 'Divide by 1000',
        withoutCents: 'Without cents',
        negativeNumbers: 'Negative numbers',
        fixedLayout: 'This report has a fixed layout.',
        noFilters: 'This report has no filters.',
        header: 'Header',
        companyName: 'Company name',
        reportTitle: 'Report title',
        subtitle: 'Subtitle',
        footer: 'Footer',
        datePrepared: 'Date prepared',
        timePrepared: 'Time prepared',
        reportBasis: 'Report basis',
        footerText: 'Footer text',
        columnsBy: 'Display columns by',
        totalOnly: 'Total only',
        months: 'Months',
        quarters: 'Quarters',
        years: 'Years',
        customersSoon: 'Customers (coming soon)',
        vendorsSoon: 'Vendors (coming soon)',
        showRows: 'Show rows',
        activeRows: 'Active rows',
        nonzeroRows: 'Non-zero rows',
        allRows: 'All rows',
        compare: 'Compare another period',
        previousPeriod: 'Previous period (PP)',
        previousYear: 'Previous year (PY)',
        change: 'Change and % change',
        filters: {
          accounts: 'Account',
          contacts: 'Customer/Vendor',
          types: 'Transaction type',
          users: 'User',
          actions: 'Event',
          entities: 'Record type',
          classes: 'Class',
          locations: 'Location',
          projects: 'Project',
          tags: 'Tag',
          items: 'Product/service',
        },
        notSpecified: 'Not specified',
        withoutProject: 'Without project',
        notTagged: 'Not tagged',
        all: 'All',
        find: 'Find {{what}}',
        nothingToChoose: 'Nothing to choose.',
        clear: 'Clear',
        selected_one: '1 selected',
        selected_other: '{{count}} selected',
      },
      table: {
        collapseRow: 'Collapse {{name}}',
        expandRow: 'Expand {{name}}',
        hideChanges: 'Hide changes',
        showChanges: 'Show changes',
        noFieldValues: 'No field values recorded.',
        field: 'Field',
        before: 'Before',
        after: 'After',
      },
      save: {
        visibility: {
          me: 'Only me',
          staff: 'All accountants on this client',
          all: 'Everyone with access to this client',
        },
        saved: 'Custom report "{{name}}" saved under Custom reports',
        saveTitle: 'Save customisation',
        editTitle: 'Edit custom report',
        saveDescription:
          'Saves the period, columns, filters, format, header and footer under Custom reports.',
        editDescription: 'Change the name or who can see this custom report.',
        nameLabel: 'Custom report name',
        shareWith: 'Share with',
        shareHint: 'Only your accounting team can share custom reports.',
        saveAsNew: 'Save as new',
      },
      // reports-en-end
    },
    ai: {
      status: {
        processing: 'Processing',
        forReview: 'For review',
        recorded: 'Recorded',
        rejected: 'Rejected',
        failed: 'Failed',
        all: 'All',
      },
      recordAs: {
        bill: 'Bill',
        expense: 'Expense',
        journalEntry: 'Journal entry',
        invoice: 'Invoice',
      },
      source: {
        jev: 'TypeSafe Jev',
        history: 'Vendor history',
        rule: 'Rules',
        user: 'You',
      },
      band: { high: 'High', medium: 'Medium', low: 'Low' },
      lowField: {
        customer: 'Customer',
        vendor: 'Vendor',
        invoiceDate: 'Invoice date',
        billDate: 'Bill date',
        recordAs: 'Record as',
        lineAccount: 'Line {{n}} account',
        lineTaxCode: 'Line {{n}} tax code',
      },
      chip: {
        edited: 'Edited',
        checked: 'Checked',
        value: '{{band}} {{pct}}',
        aria: '{{band}} confidence, {{pct}}. Why?',
        whyTitle: 'Why this value',
        suggestedBy: 'Suggested by {{source}} with {{pct}} confidence.',
        checkLow: 'Check it against the document before approving.',
        alternatives: 'Alternatives',
        confirm: "I've checked this value",
      },
      file: {
        document: 'Document',
        preview: 'Preview',
        extractedText: 'Extracted text',
        loading: 'Loading the file…',
        readPdf: 'Read from the PDF text layer',
        readOcr: 'Read by OCR (tesseract.js)',
        readOcrConfidence: 'Read by OCR (tesseract.js), {{pct}}% mean confidence',
        notRead: 'Not read yet',
        pages_one: '{{pages}} of {{count}} page',
        pages_other: '{{pages}} of {{count}} pages',
        chipHint:
          'Values marked with a chip were suggested by {{source}}; click a chip to see why.',
      },
      list: {
        title: 'AI Accountant',
        description:
          'Upload bills, receipts and sales invoices. Each one is read, matched to a vendor or customer and categorised against the chart of accounts, then waits for you to review and approve it.',
        staffOnly: 'Only accounting staff use the AI Accountant',
        uploadedOne: '{{name}} uploaded. The AI Accountant is reading it.',
        uploadedMany_one: '{{count}} files uploaded. The AI Accountant is reading them.',
        uploadedMany_other: '{{count}} files uploaded. The AI Accountant is reading them.',
        sentAgain: 'Sent to the AI Accountant again.',
        fileTypes: 'PDF, JPG or PNG',
        uploading: 'Uploading…',
        dropLabel: 'Drag and drop bills, receipts and invoices here, or',
        notAccepted: '{{name}} is not a PDF, JPG or PNG.',
        engineJev: 'Categorised by TypeSafe Jev ({{model}})',
        engineRules: 'Categorised by rules (vendor history and keywords)',
        privacyJev:
          'Only the text read from each file and the names in your chart of accounts and vendor list are sent; never the file.',
        typesafeAvailable:
          'TypeSafe is available. Switch it on for this client in Company settings › Advanced.',
        nothingLeaves: 'Nothing leaves this server.',
        maxPages_one: 'Up to {{count}} pages are read per file. A person approves every entry.',
        maxPages_other: 'Up to {{count}} pages are read per file. A person approves every entry.',
        statusAria: 'Status',
        loadError: 'Could not load the documents',
        nothingToReview: 'Nothing to review',
        noDocuments: 'No documents here',
        emptyHint: 'Uploaded bills, receipts and invoices appear here once they are read.',
        colUploaded: 'Uploaded',
        colFile: 'File',
        colParty: 'Vendor / customer',
        colDate: 'Date',
        colTotal: 'Total',
        colRecordAs: 'Record as',
        colConfidence: 'Confidence',
        colStatus: 'Status',
        colAction: 'Action',
        reading: 'Reading…',
        review: 'Review',
        rerun: 'Re-run AI',
        docExpense: 'Expense {{number}}',
        docInvoice: 'Invoice {{number}}',
        docBill: 'Bill {{number}}',
        banking: 'Banking ({{lines}})',
        journal: 'Journal {{number}}',
        view: 'View',
      },
      review: {
        loadError: 'Could not load the document',
        back: 'AI Accountant',
        uploaded: 'Uploaded {{date}}',
        uploadedBy: 'Uploaded {{date}} by {{name}}',
        fromInbox: 'from the document inbox',
        fromInboxBy: 'from the document inbox ({{name}})',
        suggestedJev: 'Suggested by TypeSafe Jev',
        suggestedJevModel: 'Suggested by TypeSafe Jev ({{model}})',
        suggestedRules: 'Suggested by rules',
        suggestedHistory: 'Suggested by history and rules',
        processing:
          'The AI Accountant is reading this document. This page updates when it is ready.',
        failed:
          'Could not process this document: {{error}} Re-run it, or reject it and record it by hand.',
        failedNoError:
          'Could not process this document. Re-run it, or reject it and record it by hand.',
        rejected: 'Rejected: {{reason}}',
        rejectedBy: 'Rejected by {{name}}: {{reason}}',
        backToReview: 'Back to review',
        recordedAs: 'Recorded as',
        recordedByAs: 'Recorded by {{name}} as',
        recordedOnAs: 'Recorded on {{date}} as',
        recordedByOnAs: 'Recorded by {{name}} on {{date}} as',
        draftNumber: '(draft)',
        journalEntryNo: 'Journal entry {{number}}',
        saved: 'Saved for later. Nothing has been recorded yet.',
        created: '{{kind}} {{number}} created from {{file}}.',
        rejectedToast: '{{name}} rejected.',
        whatToRecord: 'What to record',
        inboxJournalTitle:
          'An upload from the document inbox is recorded as a bill, an expense or an invoice',
        invoiceNo: 'Invoice no.',
        billNo: 'Bill no.',
        asPrinted: 'As printed on the invoice',
        journalDate: 'Journal date',
        dueDate: 'Due date',
        placeOfSupply: 'Place of supply',
        currency: 'Currency',
        paidFrom: 'Paid from',
        chooseAccount: 'Choose an account',
        creditAccount: 'Credit account',
        accountToCredit: 'Account to credit',
        amountsAre: 'Amounts are',
        exclusive: 'Exclusive of tax',
        inclusive: 'Inclusive of tax',
        lines: 'Lines',
        colAccount: 'Account',
        colDescription: 'Description',
        colAmount: 'Amount',
        colTaxCode: 'Tax code',
        lineDescription: 'Line {{n}} description',
        lineAmount: 'Line {{n}} amount',
        noTax: 'No tax',
        deleteLine: 'Delete line {{n}}',
        addLine: 'Add line',
        gstPlace: 'CGST and SGST, or IGST, follow from the place of supply.',
        gstPlaceInter:
          'CGST and SGST, or IGST, follow from the place of supply (IGST: another state).',
        gstPlaceIntra:
          'CGST and SGST, or IGST, follow from the place of supply (CGST + SGST: same state).',
        gstVendor: "CGST and SGST, or IGST, follow from the vendor's state.",
        gstVendorInter:
          "CGST and SGST, or IGST, follow from the vendor's state (IGST: another state).",
        gstVendorIntra:
          "CGST and SGST, or IGST, follow from the vendor's state (CGST + SGST: same state).",
        subtotal: 'Subtotal',
        tax: 'Tax',
        total: 'Total',
        printedTotal: "The document's total reads {{amount}}.",
        usage_one: '{{count}} TypeSafe request, {{tokens}} input tokens.',
        usage_other: '{{count}} TypeSafe requests, {{tokens}} input tokens.',
        reject: 'Reject…',
        missingInvoiceNo: 'Type the invoice number printed on it.',
        lowFields_one: '1 field is marked Low: check it against the file.',
        lowFields_other: '{{count}} fields are marked Low: check them against the file.',
        saveForLater: 'Save for later',
        creating: 'Creating…',
        approve: 'Approve and create',
        approveLowTitle_one: 'Approve with 1 Low field?',
        approveLowTitle_other: 'Approve with {{count}} Low fields?',
        approveLowBody:
          'The AI Accountant was not sure of these. Check each against the file before you approve; approving confirms them as they are.',
        goBack: 'Go back and check',
        confirmApprove: 'Confirm and approve',
        rejectTitle: 'Reject {{name}}?',
        rejectBody: 'Nothing is recorded. You can bring it back to review later.',
        rejectConfirm: 'Reject',
        newVendor: 'New vendor: {{name}}',
        newCustomer: 'New customer: {{name}}',
        newVendorName: 'New vendor name',
        newCustomerName: 'New customer name',
        newVendorGstin: 'New vendor GSTIN',
        newCustomerGstin: 'New customer GSTIN',
        gstinOptional: 'GSTIN (optional)',
        existingVendor: 'Choose an existing vendor instead',
        existingCustomer: 'Choose an existing customer instead',
        chooseVendor: 'Choose a vendor',
        chooseCustomer: 'Choose a customer',
        addVendorNamed: 'Add vendor {{name}}',
        addCustomerNamed: 'Add customer {{name}}',
        addNewVendor: 'Add new vendor',
        addNewCustomer: 'Add new customer',
        voucherTitle: 'Journal entry it will post',
        updating: 'Updating',
        fillLines: 'Fill in the lines to see the entry.',
        debits: 'Debits',
        credits: 'Credits',
        balanced: 'Balanced',
        outOfBalance: 'Out of balance',
      },
      statement: {
        bankStatement: 'Bank statement',
        periodRange: '{{from}} to {{to}}',
        transactionsRead_one: '{{count}} transaction read',
        transactionsRead_other: '{{count}} transactions read',
        sent: 'Sent to Banking:',
        sentBy: 'Sent to Banking by {{name}}:',
        sentOn: 'Sent to Banking on {{date}}:',
        sentByOn: 'Sent to Banking by {{name}} on {{date}}:',
        transactions_one: '{{count}} transaction.',
        transactions_other: '{{count}} transactions.',
        reviewInBanking: 'Review them in Banking',
        transactionsAria: 'Transactions',
        checksOk: 'Every row agrees with the running balance.',
        bankAccount: 'Bank account',
        chooseBank: 'Choose the bank or card account',
        openingBalance: 'Opening balance',
        closingBalance: 'Closing balance',
        selfTransfer_one:
          'Row {{rows}} is a transfer to this same account; choose another category or leave it out.',
        selfTransfer_other:
          'Rows {{rows}} are transfers to this same account; choose another category or leave them out.',
        currencyMismatch: 'The statement is in {{statement}}; this account is in {{account}}.',
        show: 'Show',
        needsPerson: 'Needs a person',
        changed: 'Changed',
        includeAll: 'Include every transaction',
        includeRow: 'Include {{description}}',
        colDate: 'Date',
        colDescription: 'Description',
        colSpent: 'Spent',
        colReceived: 'Received',
        colBalance: 'Balance',
        colCategory: 'Category',
        ref: 'Ref {{ref}}',
        checkAmount: 'Check amount',
        inOrOut: 'In or out?',
        categoryFor: 'Category for {{description}}',
        chooseCategory: 'Choose a category',
        transferTo: 'Transfer: {{account}}',
        transferGroup: 'Transfer',
        nothingPosted: 'Nothing is posted here: each line is accepted in Banking.',
        send: 'Send {{n}} to Banking',
        sentToast_one: '{{count}} transaction sent to Banking for review.',
        sentToast_other: '{{count}} transactions sent to Banking for review.',
        sentToastSkipped_one:
          '{{count}} transaction sent to Banking for review; {{skipped}} already there were skipped.',
        sentToastSkipped_other:
          '{{count}} transactions sent to Banking for review; {{skipped}} already there were skipped.',
        rejectBody: 'Nothing is sent to Banking. You can bring it back to review later.',
      },
      // ai-en-end
    },
    dashboard: {
      title: 'Dashboard',
      overview: 'Business overview',
      overviewFor: 'Business overview · {{company}}',
      selectHint: 'Choose a client to see its business overview.',
      greetingMorning: 'Good morning, {{name}}',
      greetingAfternoon: 'Good afternoon, {{name}}',
      greetingEvening: 'Good evening, {{name}}',
      periodLabel: 'Dashboard period',
      loadError: 'Could not load the dashboard',
      kpi: {
        netProfit: 'Net profit',
        changeOnPrevious: '{{change}} on the previous period',
        cashInBank: 'Cash in bank',
        accounts_one: '{{count}} accounts',
        accounts_other: '{{count}} accounts',
        owedToYou: 'Owed to you',
        overdueOpen: '{{overdue}} overdue · {{open}} open',
        toReview: 'To review',
        bankWaiting: 'Bank transactions waiting',
      },
      quick: {
        title: 'Quick actions',
        createInvoice: 'Create invoice',
        recordExpense: 'Record expense',
        importBankStatement: 'Import bank statement',
        journalEntry: 'Journal entry',
      },
      gst: {
        title: 'GST',
        viewGstr3b: 'View GSTR-3B',
        goToGst: 'Go to GST',
        none: 'No GST activity this financial year.',
        cashPayable: '{{month}} · cash payable after input tax credit',
        outputTax: 'Output tax',
        itc: 'Input tax credit',
        gstr1Due: 'GSTR-1 due',
        gstr3bDue: 'GSTR-3B due',
      },
      vat: {
        title: 'VAT',
        companySettings: 'Company settings',
        notRegistered:
          'This company is not VAT registered. Add its TRN in Company settings to prepare returns.',
        viewReturn: 'View return',
        prepareReturn: 'Prepare return',
        goToVat: 'Go to VAT',
        allFiled: 'Every VAT return is filed and paid.',
        netVatSoFarPayable: 'Net VAT so far (payable)',
        netVatSoFarRefundable: 'Net VAT so far (refundable)',
        netVatPayable: 'Net VAT (payable)',
        netVatRefundable: 'Net VAT (refundable)',
        vatOnSales: 'VAT on sales',
        vatOnPurchases: 'VAT on purchases',
        dueDate: 'Due date',
        overdueReturns_one: '{{count}} returns are overdue.',
        overdueReturns_other: '{{count}} returns are overdue.',
        chipOpen: 'Open',
        chipDueToday: 'Due today',
        chipDueIn_one: 'Due in {{count}} day',
        chipDueIn_other: 'Due in {{count}} days',
        chipOverdue: 'Overdue',
        chipFiled: 'Filed',
        chipPaid: 'Paid',
      },
      tasks: {
        title: 'Tasks',
        periodsLink: 'Periods and close',
        nothing: 'Nothing waiting. The books are up to date.',
        through: 'Through {{date}}',
        salesDrafts_one: '{{count}} sales draft to approve',
        salesDrafts_other: '{{count}} sales drafts to approve',
        purchaseDrafts_one: '{{count}} bill and expense to approve',
        purchaseDrafts_other: '{{count}} bills and expenses to approve',
        bankReview_one: '{{count}} bank transaction to review',
        bankReview_other: '{{count}} bank transactions to review',
        periodsToLock_one: '{{count}} month to lock',
        periodsToLock_other: '{{count}} months to lock',
        recurringDue_one: '{{count}} recurring transaction needs attention',
        recurringDue_other: '{{count}} recurring transactions need attention',
        einvoiceExceptions_one: '{{count}} e-invoice exception',
        einvoiceExceptions_other: '{{count}} e-invoice exceptions',
      },
      parallelRun: {
        listTitle: 'Parallel run comparison',
        listDescription:
          'Upload what QuickBooks reports for a month and see it beside Outss Accounting, to the fils, with an explanation for every difference.',
        allReports: 'All reports',
        compareMonth: 'Compare a month',
        chooseStart: 'Choose the start date.',
        from: 'From',
        to: 'To',
        starting: 'Starting…',
        startComparing: 'Start comparing',
        exportHint: 'Usually one calendar month. Export from QuickBooks: {{reports}}.',
        loadListError: 'Could not load the comparisons',
        noneTitle: 'No month compared yet',
        noneHint:
          "During the parallel run, compare each month's QuickBooks reports with Outss Accounting before signing it off.",
        colPeriod: 'Period',
        colQboReports: 'QuickBooks reports',
        colStatus: 'Status',
        uploadedOf: '{{uploaded}} of {{total}} uploaded',
        signedOffOn: 'Signed off {{date}}',
        signedOffOnBy: 'Signed off {{date}} by {{name}}',
        open: 'Open',
        loadError: 'Could not load the comparison',
        monthSignedOff: 'Month signed off',
        signOffWithdrawn: 'Sign-off withdrawn',
        title: 'Parallel run · {{period}}',
        description:
          'QuickBooks figures beside Outss Accounting for {{from}} – {{to}}. Differences are exact to the fils; each needs an explanation before the month is signed off.',
        allMonths: 'All months',
        export: 'Export',
        explainFirst: 'Explain every difference first',
        uploadTbFirst: 'Upload the QuickBooks trial balance first',
        withdrawSignOff: 'Withdraw sign-off',
        signOff: 'Sign off',
        noDifferences: 'No differences in the uploaded reports.',
        differencesUnexplained_one: '{{count}} difference, {{unexplained}} unexplained.',
        differencesUnexplained_other: '{{count}} differences, {{unexplained}} unexplained.',
        withdrawToChange: 'Withdraw the sign-off to change uploads or explanations.',
        onlyDifferences: 'Only differences',
        uploadToCompare: {
          trial_balance: 'Upload the QuickBooks trial balance to compare it',
          profit_and_loss: 'Upload the QuickBooks profit and loss to compare it',
          balance_sheet: 'Upload the QuickBooks balance sheet to compare it',
          ar_ageing: 'Upload the QuickBooks A/R ageing summary to compare it',
          ap_ageing: 'Upload the QuickBooks A/P ageing summary to compare it',
          vat_201: 'Upload the QuickBooks VAT 201 to compare it',
        },
        report: {
          trial_balance: 'Trial balance',
          profit_and_loss: 'Profit and loss',
          balance_sheet: 'Balance sheet',
          ar_ageing: 'A/R ageing summary',
          ap_ageing: 'A/P ageing summary',
          vat_201: 'VAT 201',
        },
        whereInQbo: {
          trial_balance: 'Reports › Trial balance, as of the last day',
          profit_and_loss: 'Reports › Profit and loss for the month',
          balance_sheet: 'Reports › Balance sheet, as of the last day',
          ar_ageing: 'Reports › A/R ageing summary, as of the last day',
          ap_ageing: 'Reports › A/P ageing summary, as of the last day',
          vat_201: 'Taxes › VAT return for the period, or its report export',
        },
        compared: '{{report}} compared',
        differences_one: '{{count}} difference',
        differences_other: '{{count}} differences',
        agrees: 'Agrees',
        notUploaded: 'Not uploaded',
        qboWhere: 'QuickBooks: {{where}}',
        remove: 'Remove',
        uploadTitle: 'Upload the QuickBooks export',
        uploadHint: 'Excel (.xlsx) or CSV, as exported',
        replaceLabel: 'Replace {{report}}',
        reading: 'Reading…',
        replace: 'Replace',
        colQuickBooks: 'QuickBooks',
        colOutss: 'Outss Accounting',
        colDifference: 'Difference',
        colExplanation: 'Explanation',
        noDifferencesShort: 'No differences.',
        nothingToCompare: 'Nothing to compare.',
        notInQbo: 'Not in QuickBooks',
        notInOutss: 'Not in Outss Accounting',
        difference: 'Difference',
        explanationFor: 'Explanation for {{line}}',
        anAccountant: 'An accountant',
        explain: 'Explain',
        unexplained: 'Unexplained',
      },
      vatReports: {
        loadError: 'Could not run the report',
        summaryTitle: 'VAT summary',
        total: 'Total',
        exceptionTitle: 'VAT exception',
        noExceptions: 'No exceptions in this period.',
        colDate: 'Date',
        colTransaction: 'Transaction',
        colName: 'Name',
        colCheck: 'Check',
        colAmount: 'Amount',
        errorPrefix: 'Error:',
        warningPrefix: 'Warning:',
        detailTitle: 'VAT detail',
        filterBox: 'Showing box {{box}}.',
        filterCode: 'Showing VAT code {{code}}.',
        filterBoxAndCode: 'Showing box {{box}} and VAT code {{code}}.',
        showAllBoxes: 'Show all boxes',
        noTransactions: 'No VAT transactions in this period.',
        colTransactionType: 'Transaction type',
        colNo: 'No.',
        colMemo: 'Memo/Description',
        colVatCode: 'VAT code',
        colVat: 'VAT',
        boxHeading: 'Box {{box}} · {{label}}',
        trn: 'TRN {{trn}}',
        totalBox: 'Total box {{box}}',
      },
      // dashboard-en-end
    },
  },
};

export const ar = {
  reportPages: {
    ...catalogue.ar,
    reports: {
      catalogue: {
        names: {
          'profit-and-loss': 'الأرباح والخسائر',
          'profit-and-loss-by-class': 'الأرباح والخسائر حسب الفئة',
          'profit-and-loss-by-location': 'الأرباح والخسائر حسب الموقع',
          'profit-and-loss-by-tag-group': 'الأرباح والخسائر حسب مجموعة الوسوم',
          'project-profitability': 'ملخص ربحية المشاريع',
          'balance-sheet': 'الميزانية العمومية',
          'cash-flow': 'قائمة التدفقات النقدية',
          'budget-vs-actuals': 'الموازنة مقابل الفعلي',
          'ar-aging-summary': 'ملخص أعمار الذمم المدينة',
          'ar-aging-detail': 'تفاصيل أعمار الذمم المدينة',
          'customer-balance-summary': 'ملخص أرصدة العملاء',
          'customer-balance-detail': 'تفاصيل أرصدة العملاء',
          'open-invoices': 'الفواتير المفتوحة',
          'sales-by-customer': 'ملخص المبيعات حسب العميل',
          'sales-by-customer-detail': 'تفاصيل المبيعات حسب العميل',
          'sales-by-item': 'ملخص المبيعات حسب المنتج/الخدمة',
          'sales-by-item-detail': 'تفاصيل المبيعات حسب المنتج/الخدمة',
          'ap-aging-summary': 'ملخص أعمار الذمم الدائنة',
          'ap-aging-detail': 'تفاصيل أعمار الذمم الدائنة',
          'vendor-balance-summary': 'ملخص أرصدة الموردين',
          'vendor-balance-detail': 'تفاصيل أرصدة الموردين',
          'inventory-valuation-summary': 'ملخص تقييم المخزون',
          'inventory-valuation-detail': 'تفاصيل تقييم المخزون',
          'physical-inventory-worksheet': 'ورقة الجرد الفعلي للمخزون',
          'open-purchase-order-list': 'قائمة أوامر الشراء المفتوحة',
          'open-purchase-order-detail': 'تفاصيل أوامر الشراء المفتوحة',
          'unpaid-bills': 'فواتير الموردين غير المدفوعة',
          'expenses-by-vendor': 'ملخص المصروفات حسب المورد',
          'purchases-by-vendor-detail': 'تفاصيل المشتريات حسب المورد',
          'purchases-by-item-detail': 'تفاصيل المشتريات حسب المنتج/الخدمة',
          'vat-201-summary': 'ملخص نموذج VAT 201',
          'vat-summary': 'ملخص ضريبة القيمة المضافة',
          'vat-detail': 'تفاصيل ضريبة القيمة المضافة',
          'vat-exception': 'استثناءات ضريبة القيمة المضافة',
          'gstr-1': 'GSTR-1',
          'gstr-3b': 'GSTR-3B',
          'gst-detail': 'تفاصيل ضريبة السلع والخدمات',
          'itc-summary': 'ملخص رصيد ضريبة المدخلات',
          'hsn-summary': 'ملخص HSN/SAC',
          'trial-balance': 'ميزان المراجعة',
          'fixed-asset-register': 'سجل الأصول الثابتة',
          'general-ledger': 'دفتر الأستاذ العام',
          'transaction-detail-by-account': 'تفاصيل المعاملات حسب الحساب',
          'transaction-list-by-date': 'قائمة المعاملات حسب التاريخ',
          journal: 'دفتر اليومية',
          'account-list': 'قائمة الحسابات',
          'reconciliation-report': 'تقارير التسوية البنكية',
          'parallel-run': 'مقارنة التشغيل المتوازي',
          'audit-log': 'سجل التدقيق',
          'voided-reversed': 'المعاملات الملغاة والمعكوسة',
        },
        descriptions: {
          'profit-and-loss': 'الإيرادات والمصروفات، والربح أو الخسارة الناتجة عنها، لفترة محددة.',
          'profit-and-loss-by-class':
            'الإيرادات والمصروفات لكل فئة، مع البنود التي بلا فئة والإجمالي.',
          'profit-and-loss-by-location':
            'الإيرادات والمصروفات لكل موقع، مع البنود التي بلا موقع والإجمالي.',
          'profit-and-loss-by-tag-group':
            'الإيرادات والمصروفات لكل وسم في مجموعة، مع البنود غير الموسومة والإجمالي.',
          'project-profitability': 'إيرادات كل مشروع وتكاليفه وربحه، مع البنود التي بلا مشروع.',
          'balance-sheet': 'ما تملكه الشركة وما عليها، وحقوق ملكيتها، في نهاية فترة.',
          'cash-flow':
            'مصادر النقدية واستخداماتها: الأنشطة التشغيلية والاستثمارية والتمويلية، مع النقدية في بداية الفترة ونهايتها.',
          'budget-vs-actuals':
            'الإيرادات والمصروفات الفعلية مقارنة بالموازنة، مع المبلغ المتجاوز للموازنة ونسبته منها.',
          'ar-aging-summary':
            'المستحق لك على كل عميل، حسب عدد أيام تأخر الفواتير عن تاريخ الاستحقاق.',
          'ar-aging-detail': 'كل فاتورة غير مدفوعة وكل رصيد دائن للعملاء، مجمّعة حسب أيام التأخر.',
          'customer-balance-summary': 'المستحق لك على كل عميل في تاريخ محدد.',
          'customer-balance-detail':
            'الفواتير غير المدفوعة والأرصدة الدائنة لكل عميل، مع المبلغ المتبقي.',
          'open-invoices': 'الفواتير التي لم تُسدَّد بالكامل بعد، مع تواريخ استحقاقها.',
          'sales-by-customer': 'مبيعاتك لكل عميل خلال فترة، قبل ضريبة القيمة المضافة.',
          'sales-by-customer-detail': 'كل عملية بيع لكل عميل خلال فترة، بنداً بنداً.',
          'sales-by-item': 'الكمية والمبيعات ومتوسط السعر لكل منتج أو خدمة خلال فترة.',
          'sales-by-item-detail': 'كل عملية بيع لكل منتج أو خدمة خلال فترة، بنداً بنداً.',
          'ap-aging-summary':
            'المستحق عليك لكل مورد، حسب عدد أيام تأخر فواتير الموردين عن تاريخ الاستحقاق.',
          'ap-aging-detail':
            'كل فاتورة مورد غير مدفوعة وكل رصيد دائن لدى الموردين، مجمّعة حسب أيام التأخر.',
          'vendor-balance-summary': 'المستحق عليك لكل مورد في تاريخ محدد.',
          'vendor-balance-detail':
            'فواتير كل مورد غير المدفوعة وأرصدته الدائنة، مع المبلغ المتبقي.',
          'inventory-valuation-summary':
            'الكمية المتوفرة وقيمة الأصل وفق طريقة FIFO ومتوسط التكلفة لكل صنف مخزون.',
          'inventory-valuation-detail': 'كل حركة مخزون مع الكمية والقيمة المتوفرة بعدها.',
          'physical-inventory-worksheet': 'أصناف المخزون مع الكمية المتوفرة وعمود للعدّ.',
          'open-purchase-order-list':
            'أوامر الشراء التي لم تُستلم بالكامل بعد، مع الكميات المتبقية.',
          'open-purchase-order-detail':
            'كل بند في أمر شراء مفتوح: الكميات المطلوبة والمستلمة والمؤجلة.',
          'unpaid-bills': 'فواتير الموردين التي لم تُسدَّد بالكامل بعد، مع تواريخ استحقاقها.',
          'expenses-by-vendor': 'ما أنفقته لدى كل مورد خلال فترة، قبل ضريبة القيمة المضافة.',
          'purchases-by-vendor-detail': 'كل عملية شراء من كل مورد خلال فترة، بنداً بنداً.',
          'purchases-by-item-detail':
            'كل عملية شراء لكل منتج أو خدمة أو فئة خلال فترة، بنداً بنداً.',
          'vat-201-summary': 'أوراق عمل إقرار VAT 201 لكل فترة، خانةً خانة.',
          'vat-summary': 'المبيعات والمشتريات وضريبة القيمة المضافة حسب رمز الضريبة لفترة محددة.',
          'vat-detail': 'كل بند وراء كل خانة في نموذج VAT 201 لفترة محددة.',
          'vat-exception':
            'بنود تحتاج إلى مراجعة قبل تقديم الإقرار: رموز ضريبية أو أرقام تسجيل ضريبي ناقصة، وحالات عدم تطابق.',
          'gstr-1':
            'التوريدات الصادرة حسب جداول GSTR-1: B2B وB2C والصادرات والتوريدات بنسبة صفرية والإشعارات الدائنة.',
          'gstr-3b':
            'التوريدات الصادرة والاحتساب العكسي ورصيد ضريبة المدخلات والضريبة المستحقة نقداً.',
          'gst-detail': 'كل مستند وراء إقرارات GST، مع القيمة الخاضعة للضريبة وIGST وCGST وSGST.',
          'itc-summary':
            'رصيد ضريبة المدخلات حسب المورد وفاتورة المورد، بما في ذلك الاحتساب العكسي.',
          'hsn-summary':
            'التوريدات الصادرة حسب رمز HSN أو SAC والنسبة، B2B وB2C (جدول 12 في GSTR-1).',
          'trial-balance': 'الرصيد المدين أو الدائن لكل حساب في تاريخ محدد؛ ويتساوى العمودان.',
          'fixed-asset-register':
            'كل أصل ثابت مع تكلفته والإهلاك المسجّل وصافي القيمة الدفترية في تاريخ محدد.',
          'general-ledger': 'كل معاملة حسب الحساب، من الرصيد الافتتاحي إلى الرصيد الختامي.',
          'transaction-detail-by-account': 'المعاملات وراء حركة كل حساب خلال فترة.',
          'transaction-list-by-date': 'سطر واحد لكل معاملة، مرتبة حسب التاريخ.',
          journal: 'كل معاملة مع بنودها المدينة والدائنة.',
          'account-list': 'دليل الحسابات مع الأنواع والأوصاف والأرصدة.',
          'reconciliation-report': 'كل تسوية بنكية مكتملة مع بنودها المسوّاة وغير المسوّاة.',
          'parallel-run':
            'ميزان المراجعة والأرباح والخسائر والميزانية العمومية وأعمار الذمم من QuickBooks بجانب Outss Accounting، مع التفسيرات.',
          'audit-log': 'من غيّر ماذا ومتى، مع القيم قبل التغيير وبعده.',
          'voided-reversed': 'المعاملات المعكوسة مع قيود عكسها، ومن عكسها ولماذا.',
        },
        categories: {
          businessOverview: 'نظرة عامة على الأعمال',
          whoOwesYou: 'المستحق لك',
          salesAndCustomers: 'المبيعات والعملاء',
          whatYouOwe: 'المستحق عليك',
          expensesAndVendors: 'المصروفات والموردون',
          vat: 'ضريبة القيمة المضافة',
          gst: 'ضريبة السلع والخدمات',
          forMyAccountant: 'لمحاسبي',
          audit: 'التدقيق',
        },
      },
      period: {
        preset: {
          all: 'جميع التواريخ',
          custom: 'مخصص',
          today: 'اليوم',
          this_week: 'هذا الأسبوع',
          this_week_to_date: 'هذا الأسبوع حتى تاريخه',
          this_month: 'هذا الشهر',
          this_month_to_date: 'هذا الشهر حتى تاريخه',
          this_quarter: 'هذا الربع',
          this_quarter_to_date: 'هذا الربع حتى تاريخه',
          this_year: 'هذه السنة المالية',
          this_year_to_date: 'هذه السنة المالية حتى تاريخه',
          yesterday: 'أمس',
          last_week: 'الأسبوع الماضي',
          last_month: 'الشهر الماضي',
          last_quarter: 'الربع الماضي',
          last_year: 'السنة المالية الماضية',
          last_7_days: 'آخر 7 أيام',
          last_30_days: 'آخر 30 يوماً',
        },
        endOf: {
          this_month: 'نهاية هذا الشهر',
          this_quarter: 'نهاية هذا الربع',
          this_year: 'نهاية هذه السنة المالية',
          last_month: 'نهاية الشهر الماضي',
          last_quarter: 'نهاية الربع الماضي',
          last_year: 'نهاية السنة المالية الماضية',
        },
        endDateRequired: 'اختر تاريخ النهاية.',
        endBeforeStart: 'تاريخ النهاية يسبق تاريخ البداية.',
        reportPeriod: 'فترة التقرير',
        asOf: 'حتى تاريخ',
        from: 'من',
        to: 'إلى',
      },
      hub: {
        title: 'التقارير',
        tabStandard: 'القياسية',
        tabCustom: 'التقارير المخصصة',
        tabCustomWithCount: 'التقارير المخصصة ({{count}})',
        tabManagement: 'التقارير الإدارية',
        find: 'ابحث عن تقرير بالاسم',
        favourites: 'المفضلة',
        addFavourite: 'إضافة {{name}} إلى المفضلة',
        removeFavourite: 'إزالة {{name}} من المفضلة',
        comingSoon: 'قريباً',
      },
      custom: {
        couldNotLoad: 'تعذّر تحميل التقارير المخصصة',
        emptyTitle: 'لا توجد تقارير مخصصة بعد',
        emptyHint: 'شغّل تقريراً وعدّله من «تخصيص»، ثم اختر «حفظ التخصيص».',
        standardReports: 'التقارير القياسية',
        colName: 'الاسم',
        colReport: 'التقرير',
        colSharedWith: 'مشارك مع',
        colCreatedBy: 'أنشأه',
        colLastModified: 'آخر تعديل',
        colAction: 'الإجراء',
        delete: 'حذف',
        noMatch: 'لا يوجد تقرير مخصص مطابق.',
        deleteTitle: 'حذف التقرير المخصص؟',
        deleteBody:
          'سيُحذف «{{name}}» لدى جميع من شوركَ معهم. لا يتأثر التقرير الأصلي ولا الدفاتر.',
        deleted: 'حُذف التقرير المخصص «{{name}}»',
      },
      route: {
        notFound: 'التقرير غير موجود',
        notAvailable: 'هذه الميزة غير متاحة بعد.',
        noSuchReport: 'لا يوجد تقرير باسم {{key}}.',
        backToList: 'العودة إلى قائمة التقارير',
        couldNotLoadPack: 'تعذّر تحميل التقرير الإداري',
      },
      page: {
        budget: 'الموازنة',
        accountingMethod: 'طريقة المحاسبة',
        accrual: 'الاستحقاق',
        cash: 'النقدي',
        expand: 'توسيع',
        collapse: 'طي',
        customise: 'تخصيص',
        saveCustomisation: 'حفظ التخصيص',
        email: 'البريد الإلكتروني',
        print: 'طباعة',
        export: 'تصدير',
        exportExcel: 'تصدير إلى Excel',
        exportPdf: 'تصدير إلى PDF',
        exportCsv: 'تصدير إلى CSV',
        exportFailed: 'فشل التصدير: {{message}}',
        couldNotRun: 'تعذّر تشغيل التقرير',
        nothingToShow: 'لا توجد بيانات لهذه الفترة.',
        accrualBasis: 'أساس الاستحقاق',
        generated: 'أُعدّ في {{when}}',
        tagGroup: 'مجموعة الوسوم',
        noTagGroups: 'لا توجد مجموعات وسوم بعد',
      },
      customise: {
        title: 'تخصيص التقرير',
        tabs: {
          general: 'عام',
          rowsColumns: 'الصفوف/الأعمدة',
          filter: 'التصفية',
          headerFooter: 'الرأس/التذييل',
        },
        numberFormat: 'تنسيق الأرقام',
        divideBy1000: 'القسمة على 1000',
        withoutCents: 'بدون كسور عشرية',
        negativeNumbers: 'الأرقام السالبة',
        fixedLayout: 'تخطيط هذا التقرير ثابت.',
        noFilters: 'لا توجد عوامل تصفية لهذا التقرير.',
        header: 'الرأس',
        companyName: 'اسم الشركة',
        reportTitle: 'عنوان التقرير',
        subtitle: 'العنوان الفرعي',
        footer: 'التذييل',
        datePrepared: 'تاريخ الإعداد',
        timePrepared: 'وقت الإعداد',
        reportBasis: 'أساس التقرير',
        footerText: 'نص التذييل',
        columnsBy: 'عرض الأعمدة حسب',
        totalOnly: 'الإجمالي فقط',
        months: 'الأشهر',
        quarters: 'الأرباع',
        years: 'السنوات',
        customersSoon: 'العملاء (قريباً)',
        vendorsSoon: 'الموردون (قريباً)',
        showRows: 'عرض الصفوف',
        activeRows: 'الصفوف النشطة',
        nonzeroRows: 'الصفوف غير الصفرية',
        allRows: 'جميع الصفوف',
        compare: 'مقارنة بفترة أخرى',
        previousPeriod: 'الفترة السابقة (PP)',
        previousYear: 'السنة السابقة (PY)',
        change: 'التغيّر ونسبة التغيّر',
        filters: {
          accounts: 'الحساب',
          contacts: 'العميل/المورد',
          types: 'نوع المعاملة',
          users: 'المستخدم',
          actions: 'الحدث',
          entities: 'نوع السجل',
          classes: 'الفئة',
          locations: 'الموقع',
          projects: 'المشروع',
          tags: 'الوسم',
          items: 'المنتج/الخدمة',
        },
        notSpecified: 'غير محدد',
        withoutProject: 'بدون مشروع',
        notTagged: 'بدون وسم',
        all: 'الكل',
        find: 'بحث عن {{what}}',
        nothingToChoose: 'لا توجد خيارات.',
        clear: 'مسح',
        selected_zero: 'لم يُحدَّد أي عنصر',
        selected_one: 'عنصر واحد محدد',
        selected_two: 'عنصران محددان',
        selected_few: '{{count}} عناصر محددة',
        selected_many: '{{count}} عنصراً محدداً',
        selected_other: '{{count}} عنصر محدد',
      },
      table: {
        collapseRow: 'طي {{name}}',
        expandRow: 'توسيع {{name}}',
        hideChanges: 'إخفاء التغييرات',
        showChanges: 'عرض التغييرات',
        noFieldValues: 'لم تُسجَّل قيم للحقول.',
        field: 'الحقل',
        before: 'قبل',
        after: 'بعد',
      },
      save: {
        visibility: {
          me: 'أنا فقط',
          staff: 'جميع المحاسبين على هذا العميل',
          all: 'كل من لديه صلاحية الوصول إلى هذا العميل',
        },
        saved: 'حُفظ التقرير المخصص «{{name}}» ضمن التقارير المخصصة',
        saveTitle: 'حفظ التخصيص',
        editTitle: 'تعديل التقرير المخصص',
        saveDescription:
          'يحفظ الفترة والأعمدة وعوامل التصفية والتنسيق والرأس والتذييل ضمن التقارير المخصصة.',
        editDescription: 'غيّر الاسم أو من يمكنه رؤية هذا التقرير المخصص.',
        nameLabel: 'اسم التقرير المخصص',
        shareWith: 'مشاركة مع',
        shareHint: 'مشاركة التقارير المخصصة متاحة لفريق المحاسبة فقط.',
        saveAsNew: 'حفظ كتقرير جديد',
      },
      // reports-ar-end
    },
    ai: {
      status: {
        processing: 'قيد المعالجة',
        forReview: 'للمراجعة',
        recorded: 'مسجَّل',
        rejected: 'مرفوض',
        failed: 'فشلت المعالجة',
        all: 'الكل',
      },
      recordAs: {
        bill: 'فاتورة مورد',
        expense: 'مصروف',
        journalEntry: 'قيد يومية',
        invoice: 'فاتورة',
      },
      source: {
        jev: 'TypeSafe Jev',
        history: 'سجل المورد',
        rule: 'القواعد',
        user: 'أنت',
      },
      band: { high: 'مرتفعة', medium: 'متوسطة', low: 'منخفضة' },
      lowField: {
        customer: 'العميل',
        vendor: 'المورد',
        invoiceDate: 'تاريخ الفاتورة',
        billDate: 'تاريخ فاتورة المورد',
        recordAs: 'التسجيل كـ',
        lineAccount: 'حساب السطر {{n}}',
        lineTaxCode: 'رمز ضريبة السطر {{n}}',
      },
      chip: {
        edited: 'معدَّل',
        checked: 'تم التحقق',
        value: '{{band}} {{pct}}',
        aria: 'ثقة {{band}}، {{pct}}. لماذا؟',
        whyTitle: 'سبب هذه القيمة',
        suggestedBy: 'اقترحها {{source}} بنسبة ثقة {{pct}}.',
        checkLow: 'تحقق منها مقابل المستند قبل الاعتماد.',
        alternatives: 'البدائل',
        confirm: 'تحققت من هذه القيمة',
      },
      file: {
        document: 'المستند',
        preview: 'معاينة',
        extractedText: 'النص المستخرج',
        loading: 'جارٍ تحميل الملف…',
        readPdf: 'قُرئ من طبقة النص في ملف PDF',
        readOcr: 'قُرئ بالتعرف الضوئي على الحروف (tesseract.js)',
        readOcrConfidence: 'قُرئ بالتعرف الضوئي على الحروف (tesseract.js)، بمتوسط ثقة {{pct}}%',
        notRead: 'لم يُقرأ بعد',
        pages_zero: '{{pages}} من {{count}} صفحة',
        pages_one: '{{pages}} من صفحة واحدة',
        pages_two: '{{pages}} من صفحتين',
        pages_few: '{{pages}} من {{count}} صفحات',
        pages_many: '{{pages}} من {{count}} صفحةً',
        pages_other: '{{pages}} من {{count}} صفحة',
        chipHint: 'القيم المعلَّمة بشارة اقترحها {{source}}؛ انقر على الشارة لمعرفة السبب.',
      },
      list: {
        title: 'المحاسب الذكي',
        description:
          'ارفع فواتير الموردين والإيصالات وفواتير المبيعات. يُقرأ كل مستند ويُطابَق مع مورد أو عميل ويُصنَّف وفق دليل الحسابات، ثم ينتظر مراجعتك واعتمادك.',
        staffOnly: 'المحاسب الذكي متاح لفريق المحاسبة فقط',
        uploadedOne: 'تم رفع {{name}}. المحاسب الذكي يقرؤه الآن.',
        uploadedMany_zero: 'لم يُرفع أي ملف.',
        uploadedMany_one: 'تم رفع ملف واحد. المحاسب الذكي يقرؤه الآن.',
        uploadedMany_two: 'تم رفع ملفين. المحاسب الذكي يقرؤهما الآن.',
        uploadedMany_few: 'تم رفع {{count}} ملفات. المحاسب الذكي يقرؤها الآن.',
        uploadedMany_many: 'تم رفع {{count}} ملفاً. المحاسب الذكي يقرؤها الآن.',
        uploadedMany_other: 'تم رفع {{count}} ملف. المحاسب الذكي يقرؤها الآن.',
        sentAgain: 'أُرسل إلى المحاسب الذكي مرة أخرى.',
        fileTypes: 'PDF أو JPG أو PNG',
        uploading: 'جارٍ الرفع…',
        dropLabel: 'اسحب فواتير الموردين والإيصالات والفواتير وأفلتها هنا، أو',
        notAccepted: 'الملف {{name}} ليس بصيغة PDF أو JPG أو PNG.',
        engineJev: 'يصنّفها TypeSafe Jev ({{model}})',
        engineRules: 'تُصنَّف بالقواعد (سجل المورد والكلمات المفتاحية)',
        privacyJev:
          'لا يُرسل إلا النص المقروء من كل ملف وأسماء دليل الحسابات وقائمة الموردين؛ ولا يُرسل الملف أبداً.',
        typesafeAvailable: 'خدمة TypeSafe متاحة. فعّلها لهذه الشركة من إعدادات الشركة › متقدم.',
        nothingLeaves: 'لا يغادر أي شيء هذا الخادم.',
        maxPages_zero: 'لا تُقرأ أي صفحة من الملف. يعتمد شخصٌ كل قيد.',
        maxPages_one: 'تُقرأ صفحة واحدة من كل ملف. يعتمد شخصٌ كل قيد.',
        maxPages_two: 'تُقرأ صفحتان كحد أقصى من كل ملف. يعتمد شخصٌ كل قيد.',
        maxPages_few: 'تُقرأ حتى {{count}} صفحات من كل ملف. يعتمد شخصٌ كل قيد.',
        maxPages_many: 'تُقرأ حتى {{count}} صفحةً من كل ملف. يعتمد شخصٌ كل قيد.',
        maxPages_other: 'تُقرأ حتى {{count}} صفحة من كل ملف. يعتمد شخصٌ كل قيد.',
        statusAria: 'الحالة',
        loadError: 'تعذّر تحميل المستندات',
        nothingToReview: 'لا يوجد ما يُراجَع',
        noDocuments: 'لا توجد مستندات هنا',
        emptyHint: 'تظهر هنا فواتير الموردين والإيصالات والفواتير المرفوعة بعد قراءتها.',
        colUploaded: 'تاريخ الرفع',
        colFile: 'الملف',
        colParty: 'المورد / العميل',
        colDate: 'التاريخ',
        colTotal: 'الإجمالي',
        colRecordAs: 'التسجيل كـ',
        colConfidence: 'الثقة',
        colStatus: 'الحالة',
        colAction: 'الإجراء',
        reading: 'جارٍ القراءة…',
        review: 'مراجعة',
        rerun: 'إعادة التشغيل بالذكاء الاصطناعي',
        docExpense: 'مصروف {{number}}',
        docInvoice: 'فاتورة {{number}}',
        docBill: 'فاتورة مورد {{number}}',
        banking: 'البنوك ({{lines}})',
        journal: 'قيد {{number}}',
        view: 'عرض',
      },
      review: {
        loadError: 'تعذّر تحميل المستند',
        back: 'المحاسب الذكي',
        uploaded: 'رُفع في {{date}}',
        uploadedBy: 'رفعه {{name}} في {{date}}',
        fromInbox: 'من صندوق المستندات الوارد',
        fromInboxBy: 'من صندوق المستندات الوارد ({{name}})',
        suggestedJev: 'اقتراح TypeSafe Jev',
        suggestedJevModel: 'اقتراح TypeSafe Jev ({{model}})',
        suggestedRules: 'اقتراح القواعد',
        suggestedHistory: 'اقتراح السجل والقواعد',
        processing: 'المحاسب الذكي يقرأ هذا المستند. ستُحدَّث هذه الصفحة عندما يجهز.',
        failed: 'تعذّرت معالجة هذا المستند: {{error}} أعد تشغيله، أو ارفضه وسجّله يدوياً.',
        failedNoError: 'تعذّرت معالجة هذا المستند. أعد تشغيله، أو ارفضه وسجّله يدوياً.',
        rejected: 'مرفوض: {{reason}}',
        rejectedBy: 'رفضه {{name}}: {{reason}}',
        backToReview: 'إعادة إلى المراجعة',
        recordedAs: 'سُجِّل على أنه',
        recordedByAs: 'سجّله {{name}} على أنه',
        recordedOnAs: 'سُجِّل في {{date}} على أنه',
        recordedByOnAs: 'سجّله {{name}} في {{date}} على أنه',
        draftNumber: '(مسودة)',
        journalEntryNo: 'قيد يومية {{number}}',
        saved: 'حُفظ لوقت لاحق. لم يُسجَّل شيء بعد.',
        created: 'تم إنشاء {{kind}} {{number}} من {{file}}.',
        rejectedToast: 'مرفوض: {{name}}',
        whatToRecord: 'ما سيُسجَّل',
        inboxJournalTitle:
          'المستند المرفوع من صندوق المستندات الوارد يُسجَّل كفاتورة مورد أو مصروف أو فاتورة',
        invoiceNo: 'رقم الفاتورة',
        billNo: 'رقم فاتورة المورد',
        asPrinted: 'كما هو مطبوع على الفاتورة',
        journalDate: 'تاريخ القيد',
        dueDate: 'تاريخ الاستحقاق',
        placeOfSupply: 'مكان التوريد',
        currency: 'العملة',
        paidFrom: 'مدفوع من',
        chooseAccount: 'اختر حساباً',
        creditAccount: 'الحساب الدائن',
        accountToCredit: 'الحساب المراد قيده دائناً',
        amountsAre: 'المبالغ',
        exclusive: 'غير شاملة الضريبة',
        inclusive: 'شاملة الضريبة',
        lines: 'الأسطر',
        colAccount: 'الحساب',
        colDescription: 'الوصف',
        colAmount: 'المبلغ',
        colTaxCode: 'رمز الضريبة',
        lineDescription: 'وصف السطر {{n}}',
        lineAmount: 'مبلغ السطر {{n}}',
        noTax: 'بدون ضريبة',
        deleteLine: 'حذف السطر {{n}}',
        addLine: 'إضافة سطر',
        gstPlace: 'تُحدَّد CGST وSGST أو IGST من مكان التوريد.',
        gstPlaceInter: 'تُحدَّد CGST وSGST أو IGST من مكان التوريد (IGST: ولاية أخرى).',
        gstPlaceIntra: 'تُحدَّد CGST وSGST أو IGST من مكان التوريد (CGST + SGST: الولاية نفسها).',
        gstVendor: 'تُحدَّد CGST وSGST أو IGST من ولاية المورد.',
        gstVendorInter: 'تُحدَّد CGST وSGST أو IGST من ولاية المورد (IGST: ولاية أخرى).',
        gstVendorIntra: 'تُحدَّد CGST وSGST أو IGST من ولاية المورد (CGST + SGST: الولاية نفسها).',
        subtotal: 'المجموع الفرعي',
        tax: 'الضريبة',
        total: 'الإجمالي',
        printedTotal: 'الإجمالي المقروء من المستند: {{amount}}.',
        usage_zero: 'لا طلبات إلى TypeSafe، {{tokens}} رمزاً مُدخلاً.',
        usage_one: 'طلب واحد إلى TypeSafe، {{tokens}} رمزاً مُدخلاً.',
        usage_two: 'طلبان إلى TypeSafe، {{tokens}} رمزاً مُدخلاً.',
        usage_few: '{{count}} طلبات إلى TypeSafe، {{tokens}} رمزاً مُدخلاً.',
        usage_many: '{{count}} طلباً إلى TypeSafe، {{tokens}} رمزاً مُدخلاً.',
        usage_other: '{{count}} طلب إلى TypeSafe، {{tokens}} رمزاً مُدخلاً.',
        reject: 'رفض…',
        missingInvoiceNo: 'اكتب رقم الفاتورة المطبوع عليها.',
        lowFields_zero: 'لا توجد حقول بثقة منخفضة.',
        lowFields_one: 'حقل واحد بثقة منخفضة: تحقق منه مقابل الملف.',
        lowFields_two: 'حقلان بثقة منخفضة: تحقق منهما مقابل الملف.',
        lowFields_few: '{{count}} حقول بثقة منخفضة: تحقق منها مقابل الملف.',
        lowFields_many: '{{count}} حقلاً بثقة منخفضة: تحقق منها مقابل الملف.',
        lowFields_other: '{{count}} حقل بثقة منخفضة: تحقق منها مقابل الملف.',
        saveForLater: 'حفظ لوقت لاحق',
        creating: 'جارٍ الإنشاء…',
        approve: 'اعتماد وإنشاء',
        approveLowTitle_zero: 'اعتماد بدون حقول منخفضة الثقة؟',
        approveLowTitle_one: 'اعتماد مع حقل واحد منخفض الثقة؟',
        approveLowTitle_two: 'اعتماد مع حقلين منخفضَي الثقة؟',
        approveLowTitle_few: 'اعتماد مع {{count}} حقول منخفضة الثقة؟',
        approveLowTitle_many: 'اعتماد مع {{count}} حقلاً منخفض الثقة؟',
        approveLowTitle_other: 'اعتماد مع {{count}} حقل منخفض الثقة؟',
        approveLowBody:
          'لم يكن المحاسب الذكي متأكداً من هذه الحقول. تحقق من كل منها مقابل الملف قبل الاعتماد؛ فالاعتماد يؤكدها كما هي.',
        goBack: 'الرجوع للتحقق',
        confirmApprove: 'تأكيد واعتماد',
        rejectTitle: 'رفض {{name}}؟',
        rejectBody: 'لن يُسجَّل شيء. يمكنك إعادته إلى المراجعة لاحقاً.',
        rejectConfirm: 'رفض',
        newVendor: 'مورد جديد: {{name}}',
        newCustomer: 'عميل جديد: {{name}}',
        newVendorName: 'اسم المورد الجديد',
        newCustomerName: 'اسم العميل الجديد',
        newVendorGstin: 'رقم GSTIN للمورد الجديد',
        newCustomerGstin: 'رقم GSTIN للعميل الجديد',
        gstinOptional: 'رقم GSTIN (اختياري)',
        existingVendor: 'اختيار مورد موجود بدلاً من ذلك',
        existingCustomer: 'اختيار عميل موجود بدلاً من ذلك',
        chooseVendor: 'اختر مورداً',
        chooseCustomer: 'اختر عميلاً',
        addVendorNamed: 'إضافة المورد {{name}}',
        addCustomerNamed: 'إضافة العميل {{name}}',
        addNewVendor: 'إضافة مورد جديد',
        addNewCustomer: 'إضافة عميل جديد',
        voucherTitle: 'قيد اليومية الذي سيُرحَّل',
        updating: 'جارٍ التحديث',
        fillLines: 'املأ الأسطر لعرض القيد.',
        debits: 'مدين',
        credits: 'دائن',
        balanced: 'متوازن',
        outOfBalance: 'غير متوازن',
      },
      statement: {
        bankStatement: 'كشف حساب بنكي',
        periodRange: 'من {{from}} إلى {{to}}',
        transactionsRead_zero: 'لم تُقرأ أي معاملة',
        transactionsRead_one: 'قُرئت معاملة واحدة',
        transactionsRead_two: 'قُرئت معاملتان',
        transactionsRead_few: 'قُرئت {{count}} معاملات',
        transactionsRead_many: 'قُرئت {{count}} معاملةً',
        transactionsRead_other: 'قُرئت {{count}} معاملة',
        sent: 'أُرسل إلى البنوك:',
        sentBy: 'أرسله {{name}} إلى البنوك:',
        sentOn: 'أُرسل إلى البنوك في {{date}}:',
        sentByOn: 'أرسله {{name}} إلى البنوك في {{date}}:',
        transactions_zero: 'لا معاملات.',
        transactions_one: 'معاملة واحدة.',
        transactions_two: 'معاملتان.',
        transactions_few: '{{count}} معاملات.',
        transactions_many: '{{count}} معاملةً.',
        transactions_other: '{{count}} معاملة.',
        reviewInBanking: 'راجعها في البنوك',
        transactionsAria: 'المعاملات',
        checksOk: 'كل سطر يطابق الرصيد الجاري.',
        bankAccount: 'الحساب البنكي',
        chooseBank: 'اختر الحساب البنكي أو حساب البطاقة',
        openingBalance: 'الرصيد الافتتاحي',
        closingBalance: 'الرصيد الختامي',
        selfTransfer_zero: 'لا توجد أسطر تحويل إلى هذا الحساب نفسه.',
        selfTransfer_one: 'السطر {{rows}} تحويل إلى هذا الحساب نفسه؛ اختر فئة أخرى أو استبعده.',
        selfTransfer_two:
          'السطران {{rows}} تحويلان إلى هذا الحساب نفسه؛ اختر فئة أخرى أو استبعدهما.',
        selfTransfer_few: 'الأسطر {{rows}} تحويلات إلى هذا الحساب نفسه؛ اختر فئة أخرى أو استبعدها.',
        selfTransfer_many:
          'الأسطر {{rows}} تحويلات إلى هذا الحساب نفسه؛ اختر فئة أخرى أو استبعدها.',
        selfTransfer_other:
          'الأسطر {{rows}} تحويلات إلى هذا الحساب نفسه؛ اختر فئة أخرى أو استبعدها.',
        currencyMismatch: 'عملة الكشف {{statement}}؛ وعملة هذا الحساب {{account}}.',
        show: 'عرض',
        needsPerson: 'تحتاج إلى مراجعة شخص',
        changed: 'معدَّلة',
        includeAll: 'تضمين كل المعاملات',
        includeRow: 'تضمين {{description}}',
        colDate: 'التاريخ',
        colDescription: 'الوصف',
        colSpent: 'المصروف',
        colReceived: 'المقبوض',
        colBalance: 'الرصيد',
        colCategory: 'الفئة',
        ref: 'المرجع {{ref}}',
        checkAmount: 'تحقق من المبلغ',
        inOrOut: 'وارد أم صادر؟',
        categoryFor: 'فئة {{description}}',
        chooseCategory: 'اختر فئة',
        transferTo: 'تحويل: {{account}}',
        transferGroup: 'تحويل',
        nothingPosted: 'لا يُرحَّل شيء هنا: يُقبل كل سطر في البنوك.',
        send: 'إرسال {{n}} إلى البنوك',
        sentToast_zero: 'لم تُرسل أي معاملة إلى البنوك.',
        sentToast_one: 'أُرسلت معاملة واحدة إلى البنوك للمراجعة.',
        sentToast_two: 'أُرسلت معاملتان إلى البنوك للمراجعة.',
        sentToast_few: 'أُرسلت {{count}} معاملات إلى البنوك للمراجعة.',
        sentToast_many: 'أُرسلت {{count}} معاملةً إلى البنوك للمراجعة.',
        sentToast_other: 'أُرسلت {{count}} معاملة إلى البنوك للمراجعة.',
        sentToastSkipped_zero: 'لم تُرسل أي معاملة إلى البنوك؛ وتم تخطي {{skipped}} موجودة مسبقاً.',
        sentToastSkipped_one:
          'أُرسلت معاملة واحدة إلى البنوك للمراجعة؛ وتم تخطي {{skipped}} موجودة مسبقاً.',
        sentToastSkipped_two:
          'أُرسلت معاملتان إلى البنوك للمراجعة؛ وتم تخطي {{skipped}} موجودة مسبقاً.',
        sentToastSkipped_few:
          'أُرسلت {{count}} معاملات إلى البنوك للمراجعة؛ وتم تخطي {{skipped}} موجودة مسبقاً.',
        sentToastSkipped_many:
          'أُرسلت {{count}} معاملةً إلى البنوك للمراجعة؛ وتم تخطي {{skipped}} موجودة مسبقاً.',
        sentToastSkipped_other:
          'أُرسلت {{count}} معاملة إلى البنوك للمراجعة؛ وتم تخطي {{skipped}} موجودة مسبقاً.',
        rejectBody: 'لن يُرسل شيء إلى البنوك. يمكنك إعادته إلى المراجعة لاحقاً.',
      },
      // ai-ar-end
    },
    dashboard: {
      title: 'لوحة المعلومات',
      overview: 'نظرة عامة على النشاط',
      overviewFor: 'نظرة عامة على النشاط · {{company}}',
      selectHint: 'اختر شركة لعرض نظرة عامة على نشاطها.',
      greetingMorning: 'صباح الخير، {{name}}',
      greetingAfternoon: 'مساء الخير، {{name}}',
      greetingEvening: 'مساء الخير، {{name}}',
      periodLabel: 'فترة لوحة المعلومات',
      loadError: 'تعذّر تحميل لوحة المعلومات',
      kpi: {
        netProfit: 'صافي الربح',
        changeOnPrevious: '{{change}} مقارنةً بالفترة السابقة',
        cashInBank: 'النقدية في البنك',
        accounts_zero: 'لا توجد حسابات',
        accounts_one: 'حساب واحد',
        accounts_two: 'حسابان',
        accounts_few: '{{count}} حسابات',
        accounts_many: '{{count}} حساباً',
        accounts_other: '{{count}} حساب',
        owedToYou: 'مستحق لك',
        overdueOpen: '{{overdue}} متأخرة · {{open}} مفتوحة',
        toReview: 'للمراجعة',
        bankWaiting: 'معاملات بنكية بانتظار المراجعة',
      },
      quick: {
        title: 'إجراءات سريعة',
        createInvoice: 'إنشاء فاتورة',
        recordExpense: 'تسجيل مصروف',
        importBankStatement: 'استيراد كشف حساب بنكي',
        journalEntry: 'قيد يومية',
      },
      gst: {
        title: 'ضريبة السلع والخدمات',
        viewGstr3b: 'عرض GSTR-3B',
        goToGst: 'الانتقال إلى ضريبة السلع والخدمات',
        none: 'لا يوجد نشاط لضريبة السلع والخدمات في هذه السنة المالية.',
        cashPayable: '{{month}} · المبلغ النقدي المستحق بعد خصم ضريبة المدخلات',
        outputTax: 'ضريبة المخرجات',
        itc: 'خصم ضريبة المدخلات',
        gstr1Due: 'استحقاق GSTR-1',
        gstr3bDue: 'استحقاق GSTR-3B',
      },
      vat: {
        title: 'ضريبة القيمة المضافة',
        companySettings: 'إعدادات الشركة',
        notRegistered:
          'هذه الشركة غير مسجلة في ضريبة القيمة المضافة. أضف رقم التسجيل الضريبي في إعدادات الشركة لإعداد الإقرارات.',
        viewReturn: 'عرض الإقرار',
        prepareReturn: 'إعداد الإقرار',
        goToVat: 'الانتقال إلى ضريبة القيمة المضافة',
        allFiled: 'جميع إقرارات ضريبة القيمة المضافة مقدَّمة ومدفوعة.',
        netVatSoFarPayable: 'صافي الضريبة حتى الآن (مستحق الدفع)',
        netVatSoFarRefundable: 'صافي الضريبة حتى الآن (قابل للاسترداد)',
        netVatPayable: 'صافي الضريبة (مستحق الدفع)',
        netVatRefundable: 'صافي الضريبة (قابل للاسترداد)',
        vatOnSales: 'الضريبة على المبيعات',
        vatOnPurchases: 'الضريبة على المشتريات',
        dueDate: 'تاريخ الاستحقاق',
        overdueReturns_zero: 'لا توجد إقرارات متأخرة.',
        overdueReturns_one: 'إقرار واحد متأخر.',
        overdueReturns_two: 'إقراران متأخران.',
        overdueReturns_few: '{{count}} إقرارات متأخرة.',
        overdueReturns_many: '{{count}} إقراراً متأخراً.',
        overdueReturns_other: '{{count}} إقرار متأخر.',
        chipOpen: 'مفتوح',
        chipDueToday: 'مستحق اليوم',
        chipDueIn_zero: 'مستحق اليوم',
        chipDueIn_one: 'مستحق خلال يوم واحد',
        chipDueIn_two: 'مستحق خلال يومين',
        chipDueIn_few: 'مستحق خلال {{count}} أيام',
        chipDueIn_many: 'مستحق خلال {{count}} يوماً',
        chipDueIn_other: 'مستحق خلال {{count}} يوم',
        chipOverdue: 'متأخر',
        chipFiled: 'مقدَّم',
        chipPaid: 'مدفوع',
      },
      tasks: {
        title: 'المهام',
        periodsLink: 'الفترات والإقفال',
        nothing: 'لا شيء بانتظارك. الدفاتر محدَّثة.',
        through: 'حتى {{date}}',
        salesDrafts_zero: 'لا توجد مسودات مبيعات للاعتماد',
        salesDrafts_one: 'مسودة مبيعات واحدة للاعتماد',
        salesDrafts_two: 'مسودتا مبيعات للاعتماد',
        salesDrafts_few: '{{count}} مسودات مبيعات للاعتماد',
        salesDrafts_many: '{{count}} مسودةً للمبيعات للاعتماد',
        salesDrafts_other: '{{count}} مسودة مبيعات للاعتماد',
        purchaseDrafts_zero: 'لا توجد فواتير موردين أو مصروفات للاعتماد',
        purchaseDrafts_one: 'فاتورة مورد أو مصروف واحد للاعتماد',
        purchaseDrafts_two: 'مستندان من فواتير الموردين والمصروفات للاعتماد',
        purchaseDrafts_few: '{{count}} مستندات من فواتير الموردين والمصروفات للاعتماد',
        purchaseDrafts_many: '{{count}} مستنداً من فواتير الموردين والمصروفات للاعتماد',
        purchaseDrafts_other: '{{count}} مستند من فواتير الموردين والمصروفات للاعتماد',
        bankReview_zero: 'لا توجد معاملات بنكية للمراجعة',
        bankReview_one: 'معاملة بنكية واحدة للمراجعة',
        bankReview_two: 'معاملتان بنكيتان للمراجعة',
        bankReview_few: '{{count}} معاملات بنكية للمراجعة',
        bankReview_many: '{{count}} معاملةً بنكية للمراجعة',
        bankReview_other: '{{count}} معاملة بنكية للمراجعة',
        periodsToLock_zero: 'لا توجد أشهر للإقفال',
        periodsToLock_one: 'شهر واحد للإقفال',
        periodsToLock_two: 'شهران للإقفال',
        periodsToLock_few: '{{count}} أشهر للإقفال',
        periodsToLock_many: '{{count}} شهراً للإقفال',
        periodsToLock_other: '{{count}} شهر للإقفال',
        recurringDue_zero: 'لا توجد معاملات متكررة تحتاج إلى متابعة',
        recurringDue_one: 'معاملة متكررة واحدة تحتاج إلى متابعة',
        recurringDue_two: 'معاملتان متكررتان تحتاجان إلى متابعة',
        recurringDue_few: '{{count}} معاملات متكررة تحتاج إلى متابعة',
        recurringDue_many: '{{count}} معاملةً متكررة تحتاج إلى متابعة',
        recurringDue_other: '{{count}} معاملة متكررة تحتاج إلى متابعة',
        einvoiceExceptions_zero: 'لا توجد استثناءات في الفوترة الإلكترونية',
        einvoiceExceptions_one: 'استثناء واحد في الفوترة الإلكترونية',
        einvoiceExceptions_two: 'استثناءان في الفوترة الإلكترونية',
        einvoiceExceptions_few: '{{count}} استثناءات في الفوترة الإلكترونية',
        einvoiceExceptions_many: '{{count}} استثناءً في الفوترة الإلكترونية',
        einvoiceExceptions_other: '{{count}} استثناء في الفوترة الإلكترونية',
      },
      parallelRun: {
        listTitle: 'مقارنة التشغيل الموازي',
        listDescription:
          'ارفع ما يُظهره QuickBooks لشهر ما واعرضه بجانب Outss Accounting، حتى آخر فلس، مع تفسير لكل فرق.',
        allReports: 'كل التقارير',
        compareMonth: 'مقارنة شهر',
        chooseStart: 'اختر تاريخ البداية.',
        from: 'من',
        to: 'إلى',
        starting: 'جارٍ البدء…',
        startComparing: 'بدء المقارنة',
        exportHint: 'عادةً شهر ميلادي واحد. صدِّر من QuickBooks: {{reports}}.',
        loadListError: 'تعذّر تحميل المقارنات',
        noneTitle: 'لم تتم مقارنة أي شهر بعد',
        noneHint:
          'خلال التشغيل الموازي، قارن تقارير QuickBooks لكل شهر مع Outss Accounting قبل اعتماده.',
        colPeriod: 'الفترة',
        colQboReports: 'تقارير QuickBooks',
        colStatus: 'الحالة',
        uploadedOf: 'تم رفع {{uploaded}} من {{total}}',
        signedOffOn: 'معتمد في {{date}}',
        signedOffOnBy: 'اعتمده {{name}} في {{date}}',
        open: 'مفتوح',
        loadError: 'تعذّر تحميل المقارنة',
        monthSignedOff: 'تم اعتماد الشهر',
        signOffWithdrawn: 'تم سحب الاعتماد',
        title: 'التشغيل الموازي · {{period}}',
        description:
          'أرقام QuickBooks بجانب Outss Accounting للفترة {{from}} – {{to}}. الفروق دقيقة حتى الفلس، ويحتاج كل فرق إلى تفسير قبل اعتماد الشهر.',
        allMonths: 'كل الأشهر',
        export: 'تصدير',
        explainFirst: 'فسِّر كل الفروق أولاً',
        uploadTbFirst: 'ارفع ميزان المراجعة من QuickBooks أولاً',
        withdrawSignOff: 'سحب الاعتماد',
        signOff: 'اعتماد',
        noDifferences: 'لا توجد فروق في التقارير المرفوعة.',
        differencesUnexplained_zero: 'لا توجد فروق، {{unexplained}} دون تفسير.',
        differencesUnexplained_one: 'فرق واحد، {{unexplained}} دون تفسير.',
        differencesUnexplained_two: 'فرقان، {{unexplained}} دون تفسير.',
        differencesUnexplained_few: '{{count}} فروق، {{unexplained}} دون تفسير.',
        differencesUnexplained_many: '{{count}} فرقاً، {{unexplained}} دون تفسير.',
        differencesUnexplained_other: '{{count}} فرق، {{unexplained}} دون تفسير.',
        withdrawToChange: 'اسحب الاعتماد لتغيير الملفات المرفوعة أو التفسيرات.',
        onlyDifferences: 'الفروق فقط',
        uploadToCompare: {
          trial_balance: 'ارفع ميزان المراجعة من QuickBooks لمقارنته',
          profit_and_loss: 'ارفع قائمة الأرباح والخسائر من QuickBooks لمقارنتها',
          balance_sheet: 'ارفع الميزانية العمومية من QuickBooks لمقارنتها',
          ar_ageing: 'ارفع ملخص أعمار الذمم المدينة من QuickBooks لمقارنته',
          ap_ageing: 'ارفع ملخص أعمار الذمم الدائنة من QuickBooks لمقارنته',
          vat_201: 'ارفع إقرار VAT 201 من QuickBooks لمقارنته',
        },
        report: {
          trial_balance: 'ميزان المراجعة',
          profit_and_loss: 'الأرباح والخسائر',
          balance_sheet: 'الميزانية العمومية',
          ar_ageing: 'ملخص أعمار الذمم المدينة',
          ap_ageing: 'ملخص أعمار الذمم الدائنة',
          vat_201: 'VAT 201',
        },
        whereInQbo: {
          trial_balance: 'Reports › Trial balance، كما في آخر يوم',
          profit_and_loss: 'Reports › Profit and loss للشهر',
          balance_sheet: 'Reports › Balance sheet، كما في آخر يوم',
          ar_ageing: 'Reports › A/R ageing summary، كما في آخر يوم',
          ap_ageing: 'Reports › A/P ageing summary، كما في آخر يوم',
          vat_201: 'Taxes › VAT return للفترة، أو تصدير تقريره',
        },
        compared: 'تمت مقارنة {{report}}',
        differences_zero: 'لا توجد فروق',
        differences_one: 'فرق واحد',
        differences_two: 'فرقان',
        differences_few: '{{count}} فروق',
        differences_many: '{{count}} فرقاً',
        differences_other: '{{count}} فرق',
        agrees: 'متطابق',
        notUploaded: 'لم يُرفع',
        qboWhere: 'QuickBooks: {{where}}',
        remove: 'إزالة',
        uploadTitle: 'ارفع ملف التصدير من QuickBooks',
        uploadHint: 'Excel (.xlsx) أو CSV، كما تم تصديره',
        replaceLabel: 'استبدال {{report}}',
        reading: 'جارٍ القراءة…',
        replace: 'استبدال',
        colQuickBooks: 'QuickBooks',
        colOutss: 'Outss Accounting',
        colDifference: 'الفرق',
        colExplanation: 'التفسير',
        noDifferencesShort: 'لا توجد فروق.',
        nothingToCompare: 'لا يوجد ما تتم مقارنته.',
        notInQbo: 'غير موجود في QuickBooks',
        notInOutss: 'غير موجود في Outss Accounting',
        difference: 'فرق',
        explanationFor: 'تفسير {{line}}',
        anAccountant: 'محاسب',
        explain: 'تفسير',
        unexplained: 'دون تفسير',
      },
      vatReports: {
        loadError: 'تعذّر عرض التقرير',
        summaryTitle: 'ملخص ضريبة القيمة المضافة',
        total: 'الإجمالي',
        exceptionTitle: 'استثناءات ضريبة القيمة المضافة',
        noExceptions: 'لا توجد استثناءات في هذه الفترة.',
        colDate: 'التاريخ',
        colTransaction: 'المعاملة',
        colName: 'الاسم',
        colCheck: 'الفحص',
        colAmount: 'المبلغ',
        errorPrefix: 'خطأ:',
        warningPrefix: 'تنبيه:',
        detailTitle: 'تفاصيل ضريبة القيمة المضافة',
        filterBox: 'عرض الخانة {{box}}.',
        filterCode: 'عرض رمز الضريبة {{code}}.',
        filterBoxAndCode: 'عرض الخانة {{box}} ورمز الضريبة {{code}}.',
        showAllBoxes: 'عرض كل الخانات',
        noTransactions: 'لا توجد معاملات خاضعة لضريبة القيمة المضافة في هذه الفترة.',
        colTransactionType: 'نوع المعاملة',
        colNo: 'الرقم',
        colMemo: 'المذكرة/الوصف',
        colVatCode: 'رمز الضريبة',
        colVat: 'الضريبة',
        boxHeading: 'الخانة {{box}} · {{label}}',
        trn: 'رقم التسجيل الضريبي {{trn}}',
        totalBox: 'إجمالي الخانة {{box}}',
      },
      // dashboard-ar-end
    },
  },
};
